import React, { useEffect } from 'react';
import { Box, Stack, Typography, Fade } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/formatters';
import { fireWinConfetti, prefersReducedMotion } from '../../../utils/crmConfetti';

export interface CrmWinCelebrationProps {
  open: boolean;
  nombre: string;
  monto: number;
  onDone: () => void;
  durationMs?: number;
}

const CrmWinCelebration: React.FC<CrmWinCelebrationProps> = ({
  open,
  nombre,
  monto,
  onDone,
  durationMs = 2800,
}) => {
  useEffect(() => {
    if (!open) return;
    if (!prefersReducedMotion()) {
      fireWinConfetti({ x: 0.5, y: 0.4 });
    }
    const t = window.setTimeout(onDone, durationMs);
    return () => window.clearTimeout(t);
  }, [open, onDone, durationMs]);

  if (!open) return null;

  return (
    <Fade in={open}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(15, 23, 42, 0.55)',
          pointerEvents: 'none',
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{
            px: 4,
            py: 3.5,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            maxWidth: 420,
            textAlign: 'center',
            animation: prefersReducedMotion() ? undefined : 'crmWinPop 0.45s ease-out',
            '@keyframes crmWinPop': {
              from: { opacity: 0, transform: 'scale(0.85)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(46,125,50,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrophyIcon sx={{ fontSize: 40, color: '#2e7d32' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32' }}>
            ¡Ganada!
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', lineHeight: 1.3 }}>
            {nombre}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {formatCurrency(monto)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gran noticia para el equipo comercial
          </Typography>
        </Stack>
      </Box>
    </Fade>
  );
};

export default CrmWinCelebration;
